interface FilterAdjustments {
  // Basic CSS Filters
  contrast?: number // 0-200 (100 is neutral)
  brightness?: number // 0-200 (100 is neutral)
  saturation?: number // 0-200 (100 is neutral)
  sepia?: number // 0-100 (0 is neutral)
  blur?: number // 0-10 (0 is neutral)
  grayscale?: number // 0-100 (0 is neutral)
  hueRotate?: number // 0-360 (0 is neutral)
  invert?: number // 0-100 (0 is neutral)
  exposure?: number // -100 to 100 (0 is neutral)

  // Advanced Effects
  shadows?: number // -100 to 100 (0 is neutral)
  highlights?: number // -100 to 100 (0 is neutral)
  temperature?: number // -100 to 100 (0 is neutral)
  vignette?: number // 0-100 (0 is neutral)
  grain?: number // 0-100 (0 is neutral)
  whites?: number // -100 to 100 (0 is neutral)
  blacks?: number // -100 to 100 (0 is neutral)
  texture?: number // -50 to 50 (0 is neutral) - reduced range for less dramatic effect

  // New Basic Adjustments
  tint?: number // -100 to 100 (0 is neutral)
  clarity?: number // -100 to 100 (0 is neutral)
  dehaze?: number // -100 to 100 (0 is neutral)
  vibrance?: number // -100 to 100 (0 is neutral)
  luminance?: number // -100 to 100 (0 is neutral)

  // Sharpening
  sharpeningAmount?: number // 0-150 (0 is neutral)
  sharpeningRadius?: number // 0.5-3.0 (1.0 is neutral)
  sharpeningDetail?: number // 0-100 (25 is neutral)
  sharpeningMasking?: number // 0-100 (0 is neutral)

  // Noise Reduction
  noiseReductionLuminance?: number // 0-100 (0 is neutral)
  noiseReductionDetail?: number // 0-100 (50 is neutral)
  noiseReductionContrast?: number // 0-100 (0 is neutral)
  noiseReductionColor?: number // 0-100 (0 is neutral)
  noiseReductionSmoothness?: number // 0-100 (50 is neutral)

  // SVG Filters
  svgBlendMode?: 'screen' | 'overlay' | 'darken'
  svgBlendColor?: string
}

type CanvasContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

// Helper to check if we are in a browser or worker environment
const isBrowserOrWorker =
  (typeof window !== 'undefined' && typeof window.document !== 'undefined') ||
  (typeof self !== 'undefined' && typeof self.postMessage === 'function')

// Helper to check if we are in the main browser thread
const isMainBrowserThread = typeof window !== 'undefined' && typeof window.document !== 'undefined'

class FilterUtils {
  private grainTextures: (HTMLCanvasElement | OffscreenCanvas)[] = []
  private static readonly NUM_GRAIN_TEXTURES = 5
  private isClient: boolean
  private sharedImageData: ImageData | null = null
  private sharedBuffer: Uint8ClampedArray | null = null
  private lastCanvasWidth: number = 0
  private lastCanvasHeight: number = 0

  constructor() {
    this.isClient = isBrowserOrWorker
    if (this.isClient) {
      this.createGrainTextures()
    }
  }

  private ensureSharedBuffer(width: number, height: number): void {
    if (width !== this.lastCanvasWidth || height !== this.lastCanvasHeight || !this.sharedBuffer) {
      this.sharedBuffer = new Uint8ClampedArray(width * height * 4)
      this.lastCanvasWidth = width
      this.lastCanvasHeight = height
    }
  }

  private getImageData(ctx: CanvasContext): ImageData {
    const width = ctx.canvas.width
    const height = ctx.canvas.height

    // Reuse existing ImageData if dimensions match
    if (
      this.sharedImageData &&
      this.sharedImageData.width === width &&
      this.sharedImageData.height === height
    ) {
      const currentData = ctx.getImageData(0, 0, width, height)
      this.sharedImageData.data.set(currentData.data)
      return this.sharedImageData
    }

    // Create new ImageData if dimensions don't match
    this.ensureSharedBuffer(width, height)
    const imageData = ctx.getImageData(0, 0, width, height)
    // Create new ImageData with the correct dimensions first
    this.sharedImageData = new ImageData(width, height)
    // Then copy the data from our shared buffer
    this.sharedImageData.data.set(this.sharedBuffer!)
    // Finally copy the actual image data
    this.sharedImageData.data.set(imageData.data)
    return this.sharedImageData
  }

  applyAdjustments(ctx: CanvasContext, adjustments: FilterAdjustments): void {
    if (!this.isClient) return

    // Reset context
    ctx.filter = 'none'
    ctx.globalCompositeOperation = 'source-over'

    // Get initial image data using shared buffer
    const imageData = this.getImageData(ctx)

    // Apply basic CSS filters first
    const filters: string[] = []

    // Basic CSS Filters with neutral values
    if (adjustments?.contrast !== undefined && adjustments.contrast !== 100) {
      filters.push(`contrast(${adjustments.contrast}%)`)
    }
    if (adjustments?.brightness !== undefined && adjustments.brightness !== 100) {
      filters.push(`brightness(${adjustments.brightness}%)`)
    }
    if (adjustments?.saturation !== undefined && adjustments.saturation !== 100) {
      filters.push(`saturate(${adjustments.saturation}%)`)
    }
    if (adjustments?.sepia !== undefined && adjustments.sepia !== 0) {
      filters.push(`sepia(${adjustments.sepia}%)`)
    }
    if (adjustments?.blur !== undefined && adjustments.blur !== 0) {
      filters.push(`blur(${adjustments.blur}px)`)
    }
    if (adjustments?.grayscale !== undefined && adjustments.grayscale !== 0) {
      filters.push(`grayscale(${adjustments.grayscale}%)`)
    }
    if (adjustments?.hueRotate !== undefined && adjustments.hueRotate !== 0) {
      filters.push(`hue-rotate(${adjustments.hueRotate}deg)`)
    }
    if (adjustments?.invert !== undefined && adjustments.invert !== 0) {
      filters.push(`invert(${adjustments.invert}%)`)
    }

    // Apply CSS filters if any
    if (filters.length > 0) {
      ctx.filter = filters.join(' ')
      ctx.drawImage(ctx.canvas, 0, 0)
      ctx.filter = 'none'
      // Update shared buffer after CSS filters
      imageData.data.set(ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height).data)
    }

    // Batch process pixel manipulations
    this.processPixelManipulations(imageData, adjustments)

    // Apply final result
    ctx.putImageData(imageData, 0, 0)

    // Apply effects that require canvas operations last
    if (adjustments?.vignette !== undefined && adjustments.vignette !== 0) {
      this.applyVignette(ctx, adjustments.vignette)
    }

    if (adjustments?.grain !== undefined && adjustments.grain !== 0) {
      this.applyGrain(ctx, adjustments.grain)
    }
  }

  private processPixelManipulations(imageData: ImageData, adjustments: FilterAdjustments): void {
    const data = imageData.data
    const length = data.length

    // Pre-calculate only the values that are defined
    const shadowsIntensity =
      adjustments.shadows !== undefined ? (adjustments.shadows + 100) / 200 : undefined
    const highlightsIntensity =
      adjustments.highlights !== undefined ? (adjustments.highlights + 100) / 200 : undefined
    const temperatureIntensity =
      adjustments.temperature !== undefined ? (adjustments.temperature + 100) / 200 : undefined
    const exposureIntensity =
      adjustments.exposure !== undefined ? adjustments.exposure / 100 : undefined
    const whitesIntensity = adjustments.whites !== undefined ? adjustments.whites / 200 : undefined // Reduced intensity
    const blacksIntensity = adjustments.blacks !== undefined ? adjustments.blacks / 200 : undefined // Reduced intensity
    const textureIntensity =
      adjustments.texture !== undefined ? Math.abs(adjustments.texture) / 100 : undefined
    const clarityIntensity =
      adjustments.clarity !== undefined ? adjustments.clarity / 100 : undefined
    const dehazeIntensity = adjustments.dehaze !== undefined ? adjustments.dehaze / 100 : undefined
    const vibranceIntensity =
      adjustments.vibrance !== undefined ? adjustments.vibrance / 100 : undefined
    const sharpeningAmount =
      adjustments.sharpeningAmount !== undefined && adjustments.sharpeningAmount > 0
        ? adjustments.sharpeningAmount / 100
        : undefined
    const noiseReductionIntensity =
      adjustments.noiseReductionLuminance !== undefined && adjustments.noiseReductionLuminance > 0
        ? adjustments.noiseReductionLuminance / 100
        : undefined

    // Skip if no adjustments are defined
    if (Object.values(adjustments).every((v) => v === undefined)) {
      return
    }

    // Process all pixel manipulations in a single pass
    for (let i = 0; i < length; i += 4) {
      let r = data[i]
      let g = data[i + 1]
      let b = data[i + 2]
      let luminance

      // Apply exposure first as it affects overall brightness
      if (exposureIntensity !== undefined) {
        const factor = exposureIntensity > 0 ? 1 + exposureIntensity : 1 / (1 - exposureIntensity)

        r = Math.min(255, Math.max(0, r * factor))
        g = Math.min(255, Math.max(0, g * factor))
        b = Math.min(255, Math.max(0, b * factor))
      }

      // Shadows and highlights
      if (shadowsIntensity !== undefined || highlightsIntensity !== undefined) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
        const shadowFactor = Math.max(0, 1 - luminance * 2)
        const highlightFactor = Math.max(0, luminance * 2 - 1)
        const totalAdjust =
          (shadowsIntensity !== undefined ? 1 + (shadowsIntensity - 0.5) * shadowFactor : 1) *
          (highlightsIntensity !== undefined
            ? 1 + (highlightsIntensity - 0.5) * highlightFactor
            : 1)

        if (totalAdjust !== 1) {
          r = Math.min(255, Math.max(0, r * totalAdjust))
          g = Math.min(255, Math.max(0, g * totalAdjust))
          b = Math.min(255, Math.max(0, b * totalAdjust))
        }
      }

      // Temperature
      if (temperatureIntensity !== undefined && temperatureIntensity !== 0.5) {
        if (temperatureIntensity > 0.5) {
          const factor = 1 + (temperatureIntensity - 0.5)
          r = Math.min(255, r * factor)
          b = Math.max(0, b * (2 - factor))
        } else {
          const factor = 1 + (0.5 - temperatureIntensity)
          r = Math.max(0, r * (2 - factor))
          b = Math.min(255, b * factor)
        }
      }

      // Whites and blacks with reduced intensity
      if (whitesIntensity !== undefined || blacksIntensity !== undefined) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255

        if (whitesIntensity !== undefined && luminance > 0.5) {
          const factor = 1 + whitesIntensity * (luminance - 0.5) * 2
          r = Math.min(255, r * factor)
          g = Math.min(255, g * factor)
          b = Math.min(255, b * factor)
        }

        if (blacksIntensity !== undefined && luminance < 0.5) {
          const factor = 1 + blacksIntensity * (0.5 - luminance) * 2
          r = Math.max(0, r * factor)
          g = Math.max(0, g * factor)
          b = Math.max(0, b * factor)
        }
      }

      // Clarity
      if (clarityIntensity !== undefined && clarityIntensity !== 0) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
        const localContrast = Math.abs(luminance - 0.5) * 2
        const clarityAdjust = 1 + clarityIntensity * localContrast
        if (clarityAdjust !== 1) {
          r = Math.min(255, Math.max(0, r * clarityAdjust))
          g = Math.min(255, Math.max(0, g * clarityAdjust))
          b = Math.min(255, Math.max(0, b * clarityAdjust))
        }
      }

      // Dehaze
      if (dehazeIntensity !== undefined && dehazeIntensity !== 0) {
        luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
        const dehazeAdjust = 1 + dehazeIntensity * (1 - luminance)
        if (dehazeAdjust !== 1) {
          r = Math.min(255, r * dehazeAdjust)
          g = Math.min(255, g * dehazeAdjust)
          b = Math.min(255, b * dehazeAdjust)
        }
      }

      // Vibrance
      if (vibranceIntensity !== undefined && vibranceIntensity !== 0) {
        const max = Math.max(r, g, b)
        const min = Math.min(r, g, b)
        const saturation = max === 0 ? 0 : (max - min) / max

        if (saturation > 0) {
          luminance = luminance || (r * 0.299 + g * 0.587 + b * 0.114) / 255
          const isNearSkinTone =
            r > g &&
            g > b &&
            r > 60 &&
            r < 255 &&
            g > 40 &&
            g < 230 &&
            b > 20 &&
            b < 180 &&
            luminance > 0.2 &&
            luminance < 0.9

          const adjustmentFactor = isNearSkinTone
            ? Math.abs(vibranceIntensity) * 0.3
            : Math.abs(vibranceIntensity) * (1 - saturation)

          if (adjustmentFactor !== 0) {
            const finalFactor = vibranceIntensity > 0 ? 1 + adjustmentFactor : 1 - adjustmentFactor
            const mid = (max + min) / 2
            r = Math.min(255, Math.max(0, mid + (r - mid) * finalFactor))
            g = Math.min(255, Math.max(0, mid + (g - mid) * finalFactor))
            b = Math.min(255, Math.max(0, mid + (b - mid) * finalFactor))
          }
        }
      }

      // Texture with reduced intensity
      if (textureIntensity !== undefined && textureIntensity !== 0) {
        const x = (i / 4) % imageData.width
        const y = Math.floor(i / 4 / imageData.width)
        const noise =
          (this.generatePerlinNoise(x * 0.05, y * 0.05) + 1) * 0.5 * textureIntensity * 0.5 // Reduced by 50%

        if (adjustments.texture! > 0) {
          r = Math.min(255, Math.max(0, r * (1 + noise * 0.25) + noise * 10)) // Reduced multipliers
          g = Math.min(255, Math.max(0, g * (1 + noise * 0.25) + noise * 10))
          b = Math.min(255, Math.max(0, b * (1 + noise * 0.25) + noise * 10))
        } else {
          r = Math.min(255, Math.max(0, r * (1 - noise * 0.15) + (255 - r) * noise * 0.05))
          g = Math.min(255, Math.max(0, g * (1 - noise * 0.15) + (255 - g) * noise * 0.05))
          b = Math.min(255, Math.max(0, b * (1 - noise * 0.15) + (255 - b) * noise * 0.05))
        }
      }

      // Write back RGB values
      data[i] = r
      data[i + 1] = g
      data[i + 2] = b

      // Sharpening (needs neighbors so uses original data)
      if (sharpeningAmount !== undefined) {
        const x = (i / 4) % imageData.width
        const y = Math.floor(i / 4 / imageData.width)

        if (x > 0 && x < imageData.width - 1 && y > 0 && y < imageData.height - 1) {
          for (let j = 0; j < 3; j++) {
            const center = data[i + j]
            const left = data[i - 4 + j]
            const right = data[i + 4 + j]
            const top = data[i - imageData.width * 4 + j]
            const bottom = data[i + imageData.width * 4 + j]

            const laplacian = center * 4 - left - right - top - bottom
            if (laplacian !== 0) {
              data[i + j] = Math.min(255, Math.max(0, center + laplacian * sharpeningAmount))
            }
          }
        }
      }

      // Noise reduction (needs neighbors so uses original data)
      if (noiseReductionIntensity !== undefined) {
        const x = (i / 4) % imageData.width
        const y = Math.floor(i / 4 / imageData.width)

        if (x > 0 && x < imageData.width - 1 && y > 0 && y < imageData.height - 1) {
          for (let j = 0; j < 3; j++) {
            const center = data[i + j]
            const left = data[i - 4 + j]
            const right = data[i + 4 + j]
            const top = data[i - imageData.width * 4 + j]
            const bottom = data[i + imageData.width * 4 + j]

            const average = (left + right + top + bottom) / 4
            if (average !== center) {
              data[i + j] = center + (average - center) * noiseReductionIntensity
            }
          }
        }
      }
    }
  }

  private createGrainTextures(): void {
    if (!this.isClient) return

    for (let i = 0; i < FilterUtils.NUM_GRAIN_TEXTURES; i++) {
      const grainCanvas = this.createGrainTexture(1920, 1080, 40)
      this.grainTextures.push(grainCanvas)
    }
  }

  private createGrainTexture(
    width: number,
    height: number,
    grainValue: number
  ): HTMLCanvasElement | OffscreenCanvas {
    if (!this.isClient) {
      // This should not happen due to the constructor check, but as a fallback:
      return isMainBrowserThread
        ? document.createElement('canvas')
        : new OffscreenCanvas(width, height)
    }

    const grainCanvas = isMainBrowserThread
      ? document.createElement('canvas')
      : new OffscreenCanvas(width, height)
    grainCanvas.width = width
    grainCanvas.height = height
    const grainCtx = grainCanvas.getContext('2d', { willReadFrequently: true }) as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D

    if (!grainCtx) return grainCanvas

    const imageData = grainCtx.createImageData(width, height)
    const data = imageData.data
    const grainOpacityFactor = 0.6

    for (let i = 0; i < data.length; i += 4) {
      const value = Math.random() * 255
      data[i] = value
      data[i + 1] = value
      data[i + 2] = value
      data[i + 3] = Math.random() * grainValue * grainOpacityFactor
    }

    grainCtx.putImageData(imageData, 0, 0)
    return grainCanvas
  }

  private applyVignette(ctx: CanvasContext, vignetteStrength: number): void {
    if (!this.isClient) return

    const width = ctx.canvas.width
    const height = ctx.canvas.height
    const centerX = width / 2
    const centerY = height / 2
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY)
    const intensity = (vignetteStrength / 100) * 0.8 // Increased base intensity

    // Create radial gradient with multiple stops for more dramatic effect
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      maxDistance * 0.3, // Inner radius at 30% for stronger center
      centerX,
      centerY,
      maxDistance
    )

    gradient.addColorStop(0, 'rgba(0,0,0,0)')
    gradient.addColorStop(0.7, `rgba(0,0,0,${intensity * 0.5})`) // Mid stop for smoother transition
    gradient.addColorStop(1, `rgba(0,0,0,${intensity})`)

    ctx.globalCompositeOperation = 'multiply' // Changed to multiply for stronger effect
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
    ctx.globalCompositeOperation = 'source-over' // Reset composite operation
  }

  private applyGrain(ctx: CanvasContext, grainStrength?: number): void {
    if (!this.isClient) return

    if (grainStrength) {
      const grainTexture = this.createGrainTexture(
        ctx.canvas.width,
        ctx.canvas.height,
        grainStrength
      )
      ctx.globalCompositeOperation = 'overlay'
      ctx.drawImage(grainTexture, 0, 0, ctx.canvas.width, ctx.canvas.height)
    } else {
      const randomIndex = Math.floor(Math.random() * this.grainTextures.length)
      const selectedGrainTexture = this.grainTextures[randomIndex]
      ctx.globalCompositeOperation = 'overlay'
      ctx.drawImage(selectedGrainTexture, 0, 0, ctx.canvas.width, ctx.canvas.height)
    }
  }

  // New method to apply SVG filters
  applySVGFilter(ctx: CanvasRenderingContext2D, svgFilter: string): void {
    // This method uses DOM elements like `new Image()` and is not worker-safe.
    // It will only run on the main browser thread.
    if (!isMainBrowserThread) {
      console.warn('SVG filters are not supported in worker threads.')
      return
    }
    // Get the current canvas data
    const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height)

    // Create a temporary canvas
    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = ctx.canvas.width
    tempCanvas.height = ctx.canvas.height
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true })
    if (!tempCtx) return

    // Put the image data on the temp canvas
    tempCtx.putImageData(imageData, 0, 0)

    // Create image with filter
    const img = new Image()
    const svgImage = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${ctx.canvas.width}" height="${ctx.canvas.height}">
        <defs>
          ${svgFilter}
        </defs>
        <image
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          filter="url(#filter)"
          href="${tempCanvas.toDataURL()}"
        />
      </svg>
    `

    // Create SVG blob and apply it
    const svgBlob = new Blob([svgImage], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(svgBlob)

    img.onload = (): void => {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  // SVG Filter Generators
  static generateSVGFilter(
    type: 'hueRotate' | 'saturate' | 'blend',
    params: {
      value?: number
      mode?: 'screen' | 'overlay' | 'darken'
      color?: string
    } = {}
  ): string {
    switch (type) {
      case 'hueRotate':
        return `
          <filter id="filter">
            <feColorMatrix type="hueRotate" values="${params.value || 0}"/>
          </filter>
        `
      case 'saturate':
        return `
          <filter id="filter">
            <feColorMatrix type="saturate" values="${(params.value || 100) / 100}"/>
          </filter>
        `
      case 'blend':
        return `
          <filter id="filter">
            <feFlood flood-color="${params.color || '#000000'}" result="flood"/>
            <feBlend mode="${params.mode || 'screen'}" in="SourceGraphic" in2="flood"/>
          </filter>
        `
      default:
        return ''
    }
  }

  // Example SVG filter presets
  static SVGFilters = {
    duotone: `
      <filter id="filter">
        <feColorMatrix type="matrix" values="
          0.33 0.33 0.33 0 0
          0.33 0.33 0.33 0 0
          0.33 0.33 0.33 0 0
          0 0 0 1 0
        "/>
      </filter>
    `,
    grayscale: `
      <filter id="filter">
        <feColorMatrix type="matrix" values="
          0.2126 0.7152 0.0722 0 0
          0.2126 0.7152 0.0722 0 0
          0.2126 0.7152 0.0722 0 0
          0 0 0 1 0
        "/>
      </filter>
    `,
    sepia: `
      <filter id="filter">
        <feColorMatrix type="matrix" values="
          0.393 0.769 0.189 0 0
          0.349 0.686 0.168 0 0
          0.272 0.534 0.131 0 0
          0 0 0 1 0
        "/>
      </filter>
    `
    // Add more static presets as needed
  }

  private generatePerlinNoise(x: number, y: number): number {
    // Implementation of improved Perlin noise
    const permutation = new Array(512)
    const p = [
      151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140, 36, 103, 30, 69,
      142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234, 75, 0, 26, 197, 62, 94, 252, 219,
      203, 117, 35, 11, 32, 57, 177, 33, 88, 237, 149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175,
      74, 165, 71, 134, 139, 48, 27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230,
      220, 105, 92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73, 209,
      76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86, 164, 100, 109, 198,
      173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38, 147, 118, 126, 255, 82, 85, 212,
      207, 206, 59, 227, 47, 16, 58, 17, 182, 189, 28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44,
      154, 163, 70, 221, 153, 101, 155, 167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79,
      113, 224, 232, 178, 185, 112, 104, 218, 246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12,
      191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49, 192, 214, 31, 181, 199, 106, 157,
      184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254, 138, 236, 205, 93, 222, 114, 67, 29,
      24, 72, 243, 141, 128, 195, 78, 66, 215, 61, 156, 180
    ]

    // Extend the permutation table by copying it
    for (let i = 0; i < 256; i++) {
      permutation[i] = p[i]
      permutation[256 + i] = p[i]
    }

    // Get unit cube that contains point
    const X = Math.floor(x) & 255
    const Y = Math.floor(y) & 255

    // Get relative x,y of point in cube
    x -= Math.floor(x)
    y -= Math.floor(y)

    // Compute fade curves for each of x,y
    const u = this.fade(x)
    const v = this.fade(y)

    // Hash coordinates of the 4 cube corners
    const A = permutation[X] + Y
    const AA = permutation[A]
    const AB = permutation[A + 1]
    const B = permutation[X + 1] + Y
    const BA = permutation[B]
    const BB = permutation[B + 1]

    // Add blended results from 4 corners of cube
    return this.lerp(
      v,
      this.lerp(u, this.grad(permutation[AA], x, y), this.grad(permutation[BA], x - 1, y)),
      this.lerp(u, this.grad(permutation[AB], x, y - 1), this.grad(permutation[BB], x - 1, y - 1))
    )
  }

  // Helper functions for Perlin noise
  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10)
  }

  private lerp(t: number, a: number, b: number): number {
    return a + t * (b - a)
  }

  private grad(hash: number, x: number, y: number): number {
    const h = hash & 15
    const grad = 1 + (h & 7) // Gradient value 1-8
    const isNeg = h & 8 // Randomly invert gradient
    return (isNeg ? -grad : grad) * x + (isNeg ? -grad : grad) * y
  }
}

const filterUtils = new FilterUtils()
export { filterUtils, type FilterAdjustments, FilterUtils }
